import lodashPadCharsStart from 'lodash/fp/padCharsStart';
import typeCheck from 'common/util/typeCheck';

import { SR_API_ACTION_APARTMENT_LOCATOR_GET_TWILIO_TOKEN } from 'api/actionTypes';

import log from 'client/lib/log';

const debug = log.debug('sunroom:lib:sunroomPhoneClient');
const formatNumberTwoDigits = lodashPadCharsStart('0')(2);

class SunroomPhoneClient {
  constructor() {
    this.state = null;
    this.device = null;
    this.clockId = null;
    this.init = this.init.bind(this);
  }

  isEnabled = () => {
    return !!global.Twilio;
  };

  async init({ state, apiClient }) {
    debug('[init]');

    typeCheck('state::NonEmptyObject', state);
    typeCheck('apiClient::NonEmptyObject', apiClient);

    if (this.device) {
      return;
    }

    this.state = state;

    try {
      if (!state.get('currentUser').secondaryPhone) {
        throw new Error('Secondary Phone is not configured.');
      }

      if (!global.Twilio) {
        throw new Error('Twilio library was not loaded correctly. Check your Twilio setup.');
      }

      const token = await apiClient.post(SR_API_ACTION_APARTMENT_LOCATOR_GET_TWILIO_TOKEN);

      this.device = new global.Twilio.Device(token);
      this.device.on('ready', this.onReady);
      this.device.on('error', this.onError);
      this.device.on('connect', this.onConnect);
      this.device.on('disconnect', this.onDisconnect);
    } catch (error) {
      log.error(error);
      state.set(['apartmentLocator', 'phoneDevice', 'errorMessage'], error.message);
      state.commit();
    }
  }

  connect = (userPhone) => {
    debug(`[connect] phone: ${userPhone}`);
    if (this.device) {
      this.device.connect({ To: userPhone });

      const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
      phoneState.set('number', userPhone);
      this.state.commit();
    }
  };

  disconnect = () => {
    debug('[disconnect]');
    if (this.device) {
      this.device.disconnectAll();
    }
  };

  destroy = () => {
    debug('[destroy]');
    if (this.device) {
      this.device.destroy();
      this.device = null;
    }

    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('isReady', false);
    phoneState.set('isConnected', false);
    phoneState.set('number', null);
    phoneState.set('startedClockTimestamp', null);
    phoneState.set('clockTimestamp', null);
    this.state.commit();
  };

  clock = () => {
    debug('[clock]');
    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('clockTimestamp', Date.now());
    this.state.commit();
  };

  stopClock = () => {
    debug('[stopClock]');
    if (this.clockId) {
      clearInterval(this.clockId);
      this.clockId = null;
    }
  };

  getClockTime = () => {
    if (!this.state) {
      return '';
    }

    const { startedClockTimestamp, clockTimestamp } = this.state.get([
      'apartmentLocator',
      'phoneDevice',
    ]);
    if (!startedClockTimestamp || !clockTimestamp) {
      return '';
    }

    const diffTimeOnCall = Math.round((clockTimestamp - startedClockTimestamp) / 1000);
    const minutes = formatNumberTwoDigits(Math.floor(diffTimeOnCall / 60));
    const seconds = formatNumberTwoDigits(diffTimeOnCall % 60);

    return `${minutes}:${seconds}`;
  };

  onReady = () => {
    debug('[onReady]');
    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('isReady', true);
    this.state.commit();
  };

  onError = (error) => {
    debug('[onError]', error);
    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('errorMessage', error.message);
    this.state.commit();
  };

  onConnect = () => {
    debug('[onConnect]');
    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('isConnected', true);
    phoneState.set('startedClockTimestamp', Date.now());
    this.state.commit();

    this.clockId = setInterval(this.clock, 1000);
  };

  onDisconnect = () => {
    debug('[onDisconnect]');
    this.stopClock();

    const phoneState = this.state.select(['apartmentLocator', 'phoneDevice']);
    phoneState.set('isConnected', false);
    phoneState.set('startedClockTimestamp', null);
    phoneState.set('clockTimestamp', null);
    phoneState.set('number', null);
    this.state.commit();
  };
}

export default new SunroomPhoneClient();
